import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import SingleSelect, {
  singleSelectVariant
} from "@/components/controls/SingleSelect";
import { useCurrencies, useAccountSettings } from "@/entities/settings/hooks";
import { getCurrencySymbol } from "@/pages/deals/helpers";
import { map } from "lodash";
import { extractKeyValuePairs } from "@/pages/settings/pages/GeneralSettings";
import Text from "@/components/ui/text";

interface CurrencySelectFieldProps {
  fieldName: string;
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  showSearch?: boolean;
  variant?: singleSelectVariant;
}

const CurrencySelectField: React.FC<CurrencySelectFieldProps> = ({
  fieldName,
  value,
  onChange,
  placeholder,
  disabled = false,
  readOnly = false,
  showSearch = true,
  variant = "default"
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const currencies = useCurrencies().data ?? [];
  const accountSettingsQuery = useAccountSettings();
  const accountSettings = accountSettingsQuery.data;

  const accountCurrency = useMemo(() => {
    if (accountSettings && accountSettings.length > 0) {
      const keyValuePairs = extractKeyValuePairs(accountSettings);
      return keyValuePairs?.account_currency || "";
    }
    return "";
  }, [accountSettings]);

  const selectedValue = value ?? accountCurrency;

  const currencyOptions = useMemo(() => {
    return map(currencies, (currencyCode) => ({
      id: currencyCode,
      displayName: `${getCurrencySymbol(currencyCode)} ${currencyCode}`
    }));
  }, [currencies]);

  const handleSelection = (selectedValue: string | number) => {
    setIsOpen(false);
    onChange?.(String(selectedValue));
  };

  const formatter = (currencyValue: string | number) => {
    const code = String(currencyValue);
    return `${getCurrencySymbol(code)} ${code}`;
  };

  const effectivePlaceholder = placeholder || t("select");

  // Read-only shows the resolved currency as plain text: the row that asks for
  // it has no edit affordance to offer.
  if (readOnly) {
    const displayLabel = selectedValue
      ? formatter(selectedValue)
      : effectivePlaceholder;
    return (
      <div className="px-2 py-2 min-h-9 flex items-center">
        <Text variant={selectedValue ? "body" : "bodyTertiary"}>
          {displayLabel}
        </Text>
      </div>
    );
  }

  return (
    <SingleSelect
      fieldName={fieldName}
      options={currencyOptions}
      selectedValue={selectedValue || ""}
      onSelection={handleSelection}
      placeholder={effectivePlaceholder}
      disabled={disabled}
      showSearch={showSearch}
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      formatter={formatter}
      className="h-9"
      variant={variant}
    />
  );
};

export default CurrencySelectField;
